<template>
	<div class="gradient py-8 my-8 -mx-6 px-4 product-card-white">
		<ImageScroller label="You May Also Like" :hideButtons="false">
			<Product v-for="p in upsellItems" :product="p" :key="p.id" :hideReviews="true" :small="true" class="snap-start w-1/3 shrink-0" />
		</ImageScroller>
	</div>
</template>

<script>
import Mango from '../../helpers/mango'
import ImageScroller from '../pages/ihh-items/image-scroller.vue'
import Product from '../products/product.vue'

export default {
	components: { ImageScroller, Product },
	inject: ['store'],
	props: ['product'],
	data() {
		return {
			upsellItems: [],
			scrollerRef: null,
		}
	},
	async mounted() {
		// await this.getUpsellItems()
	},

	methods: {
		async getUpsellItems() {
			if (!this.product?.id) {
				let productIdsInCart = this.store.cart?.cartItems?.map?.((c) => c.item.bcId)
				let upsellIds = [862, 4069, 2022, 3338, 3681, 3813, 1926, 3064, 3224, 3263, 3337, 3544].filter((id) => !productIdsInCart.includes(id))
				this.upsellItems = await Mango.products({ search: { bcId: { $in: upsellIds } }, limit: 12 })
			} else {
				try {
					if (this.product?.relatedProducts?.includes(-1)) {
						console.log('Related Products: Fetching dynamic related products')
						// Dynamic related products logic
						const relatedProducts = await this.getDynamicRelatedProducts()
						console.log('Related Products: Dynamic related products fetched - ', relatedProducts.length)
						this.upsellItems = this.filterProducts(relatedProducts)
					} else {
						// Original logic for explicitly defined related products
						console.log('Related Products: Fetching explicitly defined related products')
						const products = await Mango.products({
							search: { bcId: { $in: this.product?.relatedProducts || [] }, isVisible: true },
							limit: 12,
							sort: { sortOrder: 1 },
						})
						this.upsellItems = this.filterProducts(products)
					}
				} catch (error) {
					console.error('Error in related products creation:', error)
					let productIdsInCart = this.store.cart?.cartItems?.map?.((c) => c.item.bcId)
					let upsellIds = [862, 4069, 2022, 3338, 3681, 3813, 1926, 3064, 3224, 3263, 3337, 3544].filter((id) => !productIdsInCart.includes(id))
					this.upsellItems = await this.filterProducts(await Mango.products({ search: { bcId: { $in: upsellIds } }, limit: 12 }))
				}
			}
		},
		async getDynamicRelatedProducts() {
			const TOTAL_RELATED_PRODUCTS = 12
			let relatedProducts = []

			// If brandId exists, get products from the same brand
			const brandId = this.product.brandId
			const categories = this.product.categories || []

			let brandProductsCount = 0
			let categoryProductsNeeded = TOTAL_RELATED_PRODUCTS

			// Step 1: Get products from the same brand if brandId exists
			if (brandId) {
				brandProductsCount = 4 // Default number of brand products to fetch

				// Get products from the same brand, excluding the current product
				console.log('Related Products: Fetching brand products')
				const brandProducts = await this.fetchBrandProducts(brandId, brandProductsCount)
				console.log('Related Products: Brand products fetched')
				for (let p of brandProducts) console.log('Related Products: ', p.name)

				// If we got fewer brand products than expected, adjust the categoryProductsNeeded
				brandProductsCount = brandProducts.length
				categoryProductsNeeded = TOTAL_RELATED_PRODUCTS - brandProductsCount

				// Add brand products to the relatedProducts array
				relatedProducts = [...brandProducts]
			}

			// Step 2: Get products from categories to complete the set
			if (categories.length > 0 && categoryProductsNeeded > 0) {
				console.log('Related Products: Fetching category products')
				const categoryProducts = await this.fetchCategoryProducts(categories, categoryProductsNeeded)
				console.log('Related Products: Category products fetched')
				for (let p of categoryProducts) console.log('Related Products: ', p.name)
				relatedProducts = [...relatedProducts, ...categoryProducts]
			}

			// Return the combined list of related products, shuffled for randomness
			return this.shuffleArray(relatedProducts).slice(0, TOTAL_RELATED_PRODUCTS)
		},

		async fetchBrandProducts(brandId, count) {
			// Generate a random skip value for randomization
			const randomSkip = Math.floor(Math.random() * 20)
			console.log('Related Products: Using random skip for brand products:', randomSkip)

			// Fetch products from the same brand, excluding the current product and out-of-stock items
			const brandProducts = await Mango.products({
				search: {
					categories: { $nin: ['66d8b83f6e195ee1d5e05680', '66d8b83f6e195ee1d5e0568a'] }, // exclude products from the birth kit categories
					brandId: brandId,
					_id: { $ne: this.product._id }, // Exclude the current product
					outOfStock: { $ne: true }, // Exclude products marked as out of stock
					isVisible: true,
				},
				limit: count + 25, // Fetch extra products to ensure we have enough after filtering out-of-stock items
				skip: randomSkip, // Skip a random number of products for randomization
				sort: { _id: Date.now() % 2 === 0 ? 1 : -1 }, // Random sort direction
			})

			// Further filter out products where all variants are out of stock
			const inStockProducts = brandProducts.filter((product) => {
				// If product doesn't track inventory by variant, it's already filtered by outOfStock above
				if (product.inventoryTracking !== 'variant') return true

				// Check if all variants are out of stock
				return !product.variants?.every((v) => v.inventoryLevel <= 0)
			})

			console.log('Related Products: Found', brandProducts.length, 'brand products, with', inStockProducts.length, 'in stock')

			// Shuffle the results for randomness and limit to the requested count
			return this.shuffleArray(inStockProducts).slice(0, count)
		},
		async fetchCategoryProducts(categoryObjects, totalNeeded) {
			const categories = categoryObjects.map((category) => category.id)
			console.log('Related Products: Fetching products for all categories at once:', categories)

			// Generate a random skip value for randomization
			const randomSkip = Math.floor(Math.random() * 20)
			console.log('Related Products: Using random skip for category products:', randomSkip)

			// Query the database once for all categories using $in operator
			// This is more efficient than querying each category separately
			const categoryProducts = await Mango.products({
				search: {
					categories: {
						$in: categories,
						$nin: ['66d8b83f6e195ee1d5e05680', '66d8b83f6e195ee1d5e0568a'], // exclude products from the birth kit categories
					},
					_id: { $ne: this.product._id }, // Exclude the current product
					outOfStock: { $ne: true }, // Exclude products marked as out of stock
					isVisible: true,
				},
				limit: totalNeeded + 35, // Fetch more extra products to account for filtering out-of-stock items
				skip: randomSkip, // Skip a random number of products for randomization
				sort: { _id: Date.now() % 2 === 0 ? 1 : -1 }, // Random sort direction
			})

			console.log('Related Products: Found', categoryProducts.length, 'products across all categories')

			// Filter out products where all variants are out of stock
			const inStockCategoryProducts = categoryProducts.filter((product) => {
				// If product doesn't track inventory by variant, it's already filtered by outOfStock above
				if (product.inventoryTracking !== 'variant') return true

				// Check if all variants are out of stock
				return !product.variants?.every((v) => v.inventoryLevel <= 0)
			})

			console.log('Related Products: After filtering, found', inStockCategoryProducts.length, 'in-stock products')

			// Shuffle the results for additional randomness
			let allCategoryProducts = this.shuffleArray(inStockCategoryProducts).slice(0, totalNeeded)

			// If we still do not have enough products, try a broader search
			if (allCategoryProducts.length < totalNeeded) {
				const remainingNeeded = totalNeeded - allCategoryProducts.length
				console.log('Related Products: Need', remainingNeeded, 'more products, fetching random products')

				// Generate a different random skip for fallback products
				const randomFallbackSkip = Math.floor(Math.random() * 20)

				// Get random products (not from this product brand or categories)
				const randomProducts = await Mango.products({
					search: {
						_id: { $ne: this.product._id },
						outOfStock: { $ne: true }, // Exclude products marked as out of stock
						isVisible: true,
					},
					limit: remainingNeeded + 30, // Get more for better randomness and to account for filtering
					skip: randomFallbackSkip, // Skip a random number of products for randomization
				})

				// Filter out any products we have already added and any out-of-stock variant products
				const uniqueRandomProducts = randomProducts.filter((product) => {
					// First check if it's already in our list
					if (allCategoryProducts.some((p) => p._id === product._id)) return false

					// Then check variant inventory if applicable
					if (product.inventoryTracking === 'variant') {
						return !product.variants?.every((v) => v.inventoryLevel <= 0)
					}

					return true
				})

				// Add the unique random products
				allCategoryProducts = [...allCategoryProducts, ...this.shuffleArray(uniqueRandomProducts).slice(0, remainingNeeded)]
			}

			return allCategoryProducts
		},

		shuffleArray(array) {
			// Fisher-Yates shuffle algorithm for randomizing array order
			const newArray = [...array]
			for (let i = newArray.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1))
				;[newArray[i], newArray[j]] = [newArray[j], newArray[i]]
			}
			return newArray
		},

		filterOutOfStock(products) {
			return products.filter((product) => {
				if (product.inventoryTracking !== 'variant') {
					if (!product.outOfStock) return true
					return false
				}

				return !product.variants?.every((v) => v.inventoryLevel <= 0)
			})
		},

		filterProducts(products) {
			// Get product IDs that are already in the cart
			const productIdsInCart = this.store.cart?.cartItems?.map?.((c) => c.item.bcId) || []

			// Filter out products that are already in the cart and out of stock products
			return this.filterOutOfStock(products.filter((product) => !productIdsInCart.includes(product.bcId)))
		},

		resetScrollPosition() {
			// Use $nextTick to ensure the DOM has updated before trying to reset scroll
			this.$nextTick(() => {
				// Find the scroller container
				const scrollerContainer = this.$el.querySelector('.overflow-x-auto')
				if (scrollerContainer) {
					// Reset scroll position to the beginning
					scrollerContainer.scrollLeft = 0
				}
			})
		},
	},

	watch: {
		product: {
			immediate: true,
			deep: true,
			handler: async function (newProduct, oldProduct) {
				await this.getUpsellItems()
				this.resetScrollPosition()
			},
		},
		'store.cart.cartItems': {
			deep: true,
			handler: async function () {
				await this.getUpsellItems()
				this.resetScrollPosition()
			},
		},
	},
}
</script>

<style lang="postcss" scoped>
/* This applies to child components */
.product-card-white >>> .product-card {
	@apply !bg-white;
}
</style>
